import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, IconButton, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchDataCategory, deleteDataCategoryCriteriaValue } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function DataCategoryClassificationCriteriaListPage() {
  const { businessDomainId, businessUnitId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetRow, setTargetRow] = useState(null);

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/data-categories/${categoryId}`;

  const loadData = useCallback(async () => {
    try {
      const d = await fetchDataCategory(categoryId);
      setCategoryName(d.dataCategoryName || "");
      const values = (d.classificationCriteriaValues || []).map((v, i) => ({
        id: i,
        criteriaName: v.classificationCriteria?.classificationCriteriaName || "",
        valueName: v.classificationCriteriaValueName || "",
        raw: v,
      }));
      setRows(values);
    } catch (e) { notify.apiError(e); }
  }, [categoryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeleteClick = (row) => { setTargetRow(row); setConfirmOpen(true); };

  const handleDelete = async () => {
    try {
      await deleteDataCategoryCriteriaValue(categoryId, targetRow.raw);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  const filteredRows = searchText
    ? rows.filter((r) => r.criteriaName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "criteriaName", headerName: t("dataCategory.classificationCriteriaName"), flex: 1, minWidth: 200 },
    { field: "valueName", headerName: t("dataCategory.classificationCriteriaValueName"), flex: 1, minWidth: 200 },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" color="error"
          onClick={(e) => { e.stopPropagation(); handleDeleteClick(params.row); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{categoryName}：{t("dataCategory.classificationCriteriaValues")}</Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/classification-criteria/add`)}>
          {t("common.add")}
        </Button>
      </Box>

      <Box sx={{ height: 400 }}>
        <DataGrid rows={filteredRows} columns={columns} disableRowSelectionOnClick hideFooterSelectedRowCount />
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </Box>
  );
}
